import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, FileDown } from "lucide-react";
import { toast } from "react-toastify";
import Button from "src/components/ui/Button";
import InvoiceDocument from "src/components/invoice/InvoiceDocument";
import publicInvoiceApis from "src/api/publicInvoice";
import { generatePDF } from "src/utils/pdfGenerator";

const PublicInvoicePage: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [order, setOrder] = useState<any>(null);
  const [tenant, setTenant] = useState<any>(null);
  const [downloading, setDownloading] = useState(false);
  const invoiceRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const load = async () => {
      if (!invoiceId) return;
      try {
        setLoading(true);
        const res = await publicInvoiceApis.getPublicInvoice(invoiceId);
        if (!res?.data?.order) {
          setNotFound(true);
          return;
        }
        setOrder(res.data.order);
        setTenant(res.data.tenant);
      } catch (error) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [invoiceId]);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      await generatePDF(invoiceRef, `invoice-${order.orderId}.pdf`);
    } catch (error) {
      toast.error("Failed to generate invoice");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loader2 className="animate-spin text-indigo-600" size={28} />
        <span className="ml-3 text-gray-700">Loading invoice...</span>
      </div>
    );
  }

  if (notFound || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p className="text-gray-500">
          This invoice link is invalid or no longer available.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-10 px-4">
      <Helmet>
        <title>Invoice {order.orderId} | Invyntra</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="max-w-[210mm] mx-auto mb-4 flex justify-end">
        <Button
          onClick={handleDownload}
          disabled={downloading}
          className="bg-indigo-600 text-white flex items-center"
        >
          {downloading ? (
            <>
              <Loader2 size={16} className="animate-spin mr-2" /> Generating...
            </>
          ) : (
            <>
              <FileDown size={16} className="mr-2" /> Download PDF
            </>
          )}
        </Button>
      </div>

      <InvoiceDocument ref={invoiceRef} order={order} tenant={tenant} />
    </div>
  );
};

export default PublicInvoicePage;
